import { ArrowRight, Clock, Target, TrendingUp, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface AuditLandingProps {
  onStart: () => void;
}

export default function AuditLanding({ onStart }: AuditLandingProps) {
  const benefits = [
    {
      icon: Target,
      title: "Identify High-ROI Opportunities",
      description: "Discover where AI creates the most leverage in your operations",
    },
    {
      icon: TrendingUp,
      title: "Personalized Roadmap",
      description: "Get a tailored snapshot based on your business context",
    },
    {
      icon: Clock,
      title: "Quick & Actionable",
      description: "Complete in under 2 minutes, receive insights immediately",
    },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
      {/* Header */}
      <div className="text-center mb-16">
        <div className="inline-block px-4 py-1 rounded-full border border-bronze/30 bg-bronze/5 mb-6 text-xs text-bronze font-bold uppercase tracking-wider">
          Free AI Transformation Audit
        </div>
        
        <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
          Where can AI create the most{" "}
          <span className="text-cyan">leverage</span>{" "}
          in your business?
        </h1>
        
        <p className="text-xl text-light-gray/90 max-w-3xl mx-auto mb-8">
          Answer 6-8 quick questions and get a tailored AI Transformation
          Snapshot in under 2 minutes.
        </p>
        
        <p className="text-base text-medium-gray mb-12">
          In 2 minutes, discover where AI delivers the fastest ROI-then get a
          tailored plan you can act on this quarter.
        </p>
        
        <Button
          onClick={onStart}
          size="lg"
          className="bg-cyan text-deep-navy hover:bg-cyan/90 hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-cyan/50 px-10 py-6 text-lg font-bold group mb-4"
        >
          Start Your Free Audit
          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Button>
        
        {/* Back to Home Link */}
        <Button
          asChild
          variant="outline"
          size="lg"
          className="border-medium-gray/30 text-foreground hover:bg-medium-gray/10 px-8 py-6 text-base"
        >
          <Link to="/">
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Home
          </Link>
        </Button>
      </div>

      {/* Benefits Grid */}
      <div className="grid md:grid-cols-3 gap-8 mb-16">
        {benefits.map((benefit, index) => (
          <div
            key={index}
            className="bg-dark-card rounded-xl p-6 border border-border/20 hover:border-bronze/40 transition-all"
          >
            <div className="w-12 h-12 rounded-lg bg-bronze/10 flex items-center justify-center mb-4">
              <benefit.icon className="w-6 h-6 text-bronze" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {benefit.title}
            </h3>
            <p className="text-sm text-light-gray/80">{benefit.description}</p>
          </div>
        ))}
      </div>

      {/* Trust Signal */}
      <div className="text-center py-8 border-t border-border/20">
        <p className="text-sm text-medium-gray mb-2">
          <strong className="text-foreground">ArqeraAI Framework:</strong>{" "}
          Strategy → Education → Development
        </p>
        <p className="text-xs text-light-gray/70">
          Where Strategy Meets AI Execution
        </p>
      </div>
    </div>
  );
}